export function formatCountdown(totalSeconds: number) {
  const safeTotalSeconds = Math.max(totalSeconds, 0)
  const minutes = Math.floor(safeTotalSeconds / 60)
  const seconds = safeTotalSeconds % 60

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export function formatShortDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export function formatTime(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

// For rows grouped under a Today/Yesterday/Month-Year section header (see
// groupTransactionsByDate). Today/Yesterday rows show time only — the header
// already says which day. Rows under a month bucket show the date instead —
// the header only narrows it to a month, not a specific day.
export function formatHistoryCaption(date: Date, locale: string, now = new Date()): string {
  const isToday = date.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()

  return isToday || isYesterday ? formatTime(date, locale) : formatShortDate(date, locale)
}

export function formatShortRelativeTime(
  date: Date,
  labels: {
    minutesAgo: (n: number) => string
    hoursAgo:   (n: number) => string
    daysAgo:    (n: number) => string
  },
  now = new Date(),
): string {
  const diffMs = now.getTime() - date.getTime()
  const mins   = Math.floor(diffMs / 60_000)
  const hours  = Math.floor(diffMs / 3_600_000)
  const days   = Math.floor(diffMs / 86_400_000)
  if (mins  < 60) return labels.minutesAgo(Math.max(1, mins))
  if (hours < 24) return labels.hoursAgo(hours)
  return labels.daysAgo(days)
}

export function formatRelativeDateTime(
  date: Date,
  now: Date,
  labels: { today: string; yesterday: string },
  locale: string,
): string {
  const isToday = date.toDateString() === now.toDateString()

  const yesterdayDate = new Date(now)
  yesterdayDate.setDate(now.getDate() - 1)
  const isYesterday = date.toDateString() === yesterdayDate.toDateString()

  const hh = date.getHours().toString().padStart(2, '0')
  const mm = date.getMinutes().toString().padStart(2, '0')

  if (isToday) return `${labels.today}, ${hh}:${mm}`
  if (isYesterday) return `${labels.yesterday}, ${hh}:${mm}`

  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
